import React, { useState, Suspense, lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { auth } from '../../../shared/auth/auth';
import Apps from '../../components/Apps/Apps';
import { AdminRoute, UserRoute } from '../../components/CustomRoutes/CustomRoutes';
import TopBar from '../../components/TopBar/TopBar';
import NotFound from '../NotFound/NotFound';
import './MainApp.scss';

const Admin = lazy(() => import('../../components/Admin/Admin'));
const ApplicationForm = lazy(() => import('../../components/ApplicationForm/ApplicationForm'));
const CategoryForm = lazy(() => import('../../components/Categories/CategoryForm'));
const UserDetails = lazy(() => import('../../components/UserDetails/UserDetails'));
const AppTour = lazy(() => import('../../components/AppTour/AppTour').then((module) => ({ default: module.AppTour })));
const FeedbackModal = lazy(() => import('../../components/FeedbackModal/FeedbackModal').then((module) => ({ default: module.FeedbackModal })));

export enum AppTabKeys {
    Category = 'category',
    AZ = 'az',
}

const MainApp: React.FC = () => {
    const [activeAppTab, setActiveAppTab] = useState<AppTabKeys>(AppTabKeys.Category);

    if (!auth.isLoggedIn()) {
        return null;
    }

    const renderApps = <Apps activeTab={activeAppTab} onSetActiveTab={setActiveAppTab} />;

    return (
        <BrowserRouter basename="/">
            <div className="AppContainer">
                <ToastContainer />
                <Suspense fallback={null}>
                    <AppTour activeTab={activeAppTab} />
                </Suspense>
                <Suspense fallback={null}>
                    <FeedbackModal />
                </Suspense>
                <TopBar />
                <header>Applications</header>
                <Suspense fallback={null}>
                    <Routes>
                        <Route path="/" element={<UserRoute>{renderApps}</UserRoute>} />
                        <Route path={`/${AppTabKeys.AZ}`} element={<UserRoute>{renderApps}</UserRoute>} />
                        <Route path={`/${AppTabKeys.Category}`} element={<UserRoute>{renderApps}</UserRoute>} />
                        <Route
                            path="/userdetails"
                            element={
                                <UserRoute>
                                    <UserDetails />
                                </UserRoute>
                            }
                        />
                        <Route path="/admin" element={<Navigate to="/admin/applications" replace={true} />} />
                        <Route
                            path="/admin/applications"
                            element={
                                <AdminRoute>
                                    <Admin />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/categories"
                            element={
                                <AdminRoute>
                                    <Admin />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/default-profile-apps"
                            element={
                                <AdminRoute>
                                    <Admin />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/analytics"
                            element={
                                <AdminRoute>
                                    <Admin />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/analytics/by-location"
                            element={
                                <AdminRoute>
                                    <Admin />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/analytics/referrers"
                            element={
                                <AdminRoute>
                                    <Admin />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/website-requests"
                            element={<Navigate to="/admin/analytics/referrers" replace={true} />}
                        />
                        <Route
                            path="/admin/feedback"
                            element={
                                <AdminRoute>
                                    <Admin />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/settings"
                            element={
                                <AdminRoute>
                                    <Admin />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/applications/:id"
                            element={
                                <AdminRoute>
                                    <ApplicationForm />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/categories/:id"
                            element={
                                <AdminRoute>
                                    <CategoryForm />
                                </AdminRoute>
                            }
                        />
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </Suspense>
            </div>
        </BrowserRouter>
    );
};

export default MainApp;
